import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Eye, Edit, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal riutilizzabile per anteprima + editing + invio email manuali.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   title: string mostrata in header (es. "Anteprima Email — Richiesta Documenti")
 *   endpoint: URL relativo backend (POST) che deve supportare {dry_run:true} per preview
 *            e {subject,html,text,...basePayload} per invio finale.
 *   basePayload: object (params extra da inviare sia in dry_run che in invio, es. {tipo:"fattura", codice_fiscale:true, ...})
 *   onSent: callback dopo invio riuscito
 *   successToast: stringa (default "Email inviata al cliente")
 */
export default function EmailPreviewModal({
  open,
  onClose,
  title = "Anteprima Email",
  endpoint,
  basePayload = {},
  onSent,
  successToast = "Email inviata al cliente",
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [to, setTo] = useState("");
  const [bcc, setBcc] = useState("");
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!open || !endpoint) return;
    let cancelled = false;
    setLoading(true);
    setEditMode(false);
    (async () => {
      try {
        const res = await axios.post(
          `${API}${endpoint}`,
          { ...basePayload, dry_run: true },
          { withCredentials: true }
        );
        if (cancelled) return;
        setSubject(res.data.subject || "");
        setHtml(res.data.html || "");
        setText(res.data.text || "");
        setTo(res.data.to || "");
        setBcc(res.data.bcc || "info@showroomingenio.it");
      } catch (e) {
        console.error("Preview load error:", e);
        const msg = e.response?.data?.detail || "Errore nel caricamento anteprima";
        toast.error(msg);
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, endpoint, JSON.stringify(basePayload)]);

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post(
        `${API}${endpoint}`,
        { ...basePayload, subject, html, text },
        { withCredentials: true }
      );
      toast.success(successToast);
      onSent?.();
      onClose?.();
    } catch (e) {
      console.error("Send error:", e);
      const msg = e.response?.data?.detail || "Errore nell'invio dell'email";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending && !v) onClose?.(); }}>
      <DialogContent
        data-testid="email-preview-modal"
        className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col overflow-hidden"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b bg-slate-50 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Eye className="w-5 h-5 text-blue-600" />
            {title}
          </DialogTitle>
          <div className="text-sm text-slate-600 pt-2 space-y-0.5">
            <div><span className="font-medium">A:</span> {to || "—"}</div>
            <div><span className="font-medium">BCC:</span> {bcc || "—"}</div>
            <div><span className="font-medium">Oggetto:</span> {subject || "—"}</div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden bg-slate-100">
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-500">
              <Loader2 className="w-6 h-6 mr-2 animate-spin" />
              Caricamento anteprima...
            </div>
          ) : editMode ? (
            <div className="h-full overflow-auto p-6 space-y-4 bg-white" data-testid="email-preview-edit-panel">
              <div>
                <Label htmlFor="epm-subject">Oggetto</Label>
                <Input
                  id="epm-subject"
                  data-testid="email-preview-edit-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="epm-html">Corpo HTML</Label>
                <Textarea
                  id="epm-html"
                  data-testid="email-preview-edit-html"
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  rows={20}
                  className="mt-1 font-mono text-xs"
                />
              </div>
              <div>
                <Label htmlFor="epm-text">Fallback plain-text (opzionale)</Label>
                <Textarea
                  id="epm-text"
                  data-testid="email-preview-edit-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={8}
                  className="mt-1 font-mono text-xs"
                />
              </div>
            </div>
          ) : (
            <iframe
              title="email-preview"
              data-testid="email-preview-iframe"
              srcDoc={html}
              sandbox=""
              className="w-full h-full border-0 bg-white"
            />
          )}
        </div>

        <div className="border-t bg-white px-6 py-3 flex flex-wrap items-center justify-between gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            data-testid="email-preview-toggle-edit"
            onClick={() => setEditMode((v) => !v)}
            disabled={loading || sending}
          >
            {editMode ? (
              <><Eye className="w-4 h-4 mr-2" /> Torna all'anteprima</>
            ) : (
              <><Edit className="w-4 h-4 mr-2" /> Modifica testo</>
            )}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="email-preview-cancel"
              onClick={() => onClose?.()}
              disabled={sending}
            >
              Annulla
            </Button>
            <Button
              type="button"
              data-testid="email-preview-confirm-send"
              onClick={handleSend}
              disabled={loading || sending || !html}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Conferma e invia</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
